import Config from '../ClientConfig';

const EMPTY_QUERY = {};

function monthlyMarginRunUrl(id) {
  if (typeof id !== 'string' || !/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(id)) {
    throw new Error('Invalid report identifier.');
  }
  return `${Config.API_URL_REPORTS_V2_MONTHLY_MARGIN_ANALYSIS}${encodeURIComponent(id)}/`;
}

function commercialAnalysisRunUrl(id) {
  if (typeof id !== 'string' || !/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(id)) {
    throw new Error('Invalid report identifier.');
  }
  return `${Config.API_URL_REPORTS_V2_COMMERCIAL_ANALYSIS}${encodeURIComponent(id)}/`;
}

function productPromotionAnalysisRunUrl(id) {
  if (typeof id !== 'string' || !/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(id)) {
    throw new Error('Invalid report identifier.');
  }
  return `${Config.API_URL_REPORTS_V2_PRODUCT_PROMOTION_ANALYSIS}${encodeURIComponent(id)}/`;
}

function productPromotionScopeQuery(scope) {
  return { ...scope, channels: [...scope.channels].sort((a, b) => a - b).join(',') };
}

function commercialAnalysisDownloadName(id, details) {
  if (!details || typeof details.location_name !== 'string'
      || !/^\d{4}-\d{2}-\d{2}$/.test(details.analysis_start)
      || !/^\d{4}-\d{2}-\d{2}$/.test(details.analysis_end)) {
    return `commercial-analysis-${id}.pdf`;
  }
  const location = details.location_name.normalize('NFKD').replace(/[\u0300-\u036f]/g, '')
    .replace(/[^A-Za-z0-9]+/g, '-').replace(/^-+|-+$/g, '')
    .slice(0, 48).replace(/-+$/g, '') || 'location';
  const shortId = id.replace(/-/g, '').slice(0, 12);
  return `commercial-analysis-${location}-${details.analysis_start}_to_${details.analysis_end}-${shortId}.pdf`;
}

function getData(client, url, queryData = EMPTY_QUERY) {
  if (!client || typeof client.getData !== 'function') {
    return null;
  }
  return client.getData(url, queryData);
}

function getDataAsync(client, url, queryData = EMPTY_QUERY) {
  if (!client || typeof client.getDataAsync !== 'function') {
    return Promise.reject(new Error('Asynchronous report transport is unavailable.'));
  }
  return client.getDataAsync(url, queryData);
}

function pushData(client, url, queryData = EMPTY_QUERY) {
  if (!client || typeof client.pushData !== 'function') {
    return null;
  }
  return client.pushData(url, queryData);
}

function pushDataAsync(client, url, queryData = EMPTY_QUERY) {
  if (!client || typeof client.requestJsonAsync !== 'function') {
    return Promise.reject(new Error('Asynchronous report transport is unavailable.'));
  }
  return client.requestJsonAsync(url, 'POST', queryData);
}

const BackofficeReportsApi = {
  getMonthlyMarginOptionsAsync(client, query = EMPTY_QUERY) {
    return getDataAsync(client, `${Config.API_URL_REPORTS_V2_MONTHLY_MARGIN_ANALYSIS}options/`, query);
  },

  getMonthlyMarginRunsAsync(client) {
    return getDataAsync(client, Config.API_URL_REPORTS_V2_MONTHLY_MARGIN_ANALYSIS);
  },

  getMonthlyMarginRunAsync(client, id) {
    return getDataAsync(client, monthlyMarginRunUrl(id));
  },

  postMonthlyMarginRunAsync(client, parameters) {
    return pushDataAsync(client, Config.API_URL_REPORTS_V2_MONTHLY_MARGIN_ANALYSIS, parameters);
  },

  async downloadMonthlyMarginPdf(client, id, signal, details = {}) {
    const token = client?.getAuthToken?.();
    if (!token) throw Object.assign(new Error('Authentication is required.'), { status: 401 });
    const response = await fetch(`${client.prefix || ''}${monthlyMarginRunUrl(id)}download/`, {
      method: 'GET', headers: { Authorization: `Bearer ${token}`, Accept: 'application/pdf, application/json' },
      credentials: 'omit', redirect: 'error', cache: 'no-store', signal,
    });
    if (!response.ok) throw Object.assign(new Error('Stored report download failed.'), { status: response.status });
    if (!(response.headers.get('Content-Type') || '').toLowerCase().startsWith('application/pdf')) {
      throw new Error('The response is not a PDF.');
    }
    const blob = await response.blob();
    if (signal?.aborted) throw Object.assign(new Error('Download was cancelled.'), { name: 'AbortError' });
    const objectUrl = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    try {
      const location = String(details.location_name || 'location').normalize('NFKD').replace(/[\u0300-\u036f]/g, '')
        .replace(/[^A-Za-z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 48) || 'location';
      const month = /^\d{4}-\d{2}$/.test(details.month) ? details.month : id;
      link.href = objectUrl;
      link.download = `monthly-margin-analysis-${location}-${month}-${id.slice(0, 8)}.pdf`;
      document.body.appendChild(link);
      link.click();
    } finally {
      link.remove();
      window.URL.revokeObjectURL(objectUrl);
    }
  },

  getProductPromotionOptionsAsync(client) {
    return getDataAsync(client, `${Config.API_URL_REPORTS_V2_PRODUCT_PROMOTION_ANALYSIS}options/`);
  },

  getProductPromotionTargetsAsync(client, query) {
    return getDataAsync(client, `${Config.API_URL_REPORTS_V2_PRODUCT_PROMOTION_ANALYSIS}targets/`, query);
  },

  getProductPromotionComparisonOptionsAsync(client, scope) {
    return getDataAsync(client, `${Config.API_URL_REPORTS_V2_PRODUCT_PROMOTION_ANALYSIS}comparison-options/`, scope);
  },

  getProductPromotionCustomerExamplesAsync(client, scope, cursor = null) {
    return getDataAsync(client, `${Config.API_URL_REPORTS_V2_PRODUCT_PROMOTION_ANALYSIS}customer-examples/`,
      { ...productPromotionScopeQuery(scope), ...(cursor ? { cursor } : {}) });
  },

  getProductPromotionCustomerHistoryAsync(client, handle, scope, mode, cursor = null) {
    if (typeof handle !== 'string' || !/^[A-Za-z0-9_-]{20,512}$/.test(handle)) {
      throw new Error('Invalid customer example handle.');
    }
    return getDataAsync(client, `${Config.API_URL_REPORTS_V2_PRODUCT_PROMOTION_ANALYSIS}customer-examples/${encodeURIComponent(handle)}/`,
      { ...productPromotionScopeQuery(scope), mode, ...(cursor ? { cursor } : {}) });
  },

  getProductPromotionRunsAsync(client) {
    return getDataAsync(client, Config.API_URL_REPORTS_V2_PRODUCT_PROMOTION_ANALYSIS);
  },

  getProductPromotionRunAsync(client, id) {
    return getDataAsync(client, productPromotionAnalysisRunUrl(id));
  },

  postProductPromotionRunAsync(client, parameters) {
    return pushDataAsync(client, Config.API_URL_REPORTS_V2_PRODUCT_PROMOTION_ANALYSIS, parameters);
  },

  async downloadProductPromotionPdf(client, id, signal, details = {}) {
    const token = client?.getAuthToken?.();
    if (!token) throw Object.assign(new Error('Authentication is required.'), { status: 401 });
    const response = await fetch(`${client.prefix || ''}${productPromotionAnalysisRunUrl(id)}download/`, {
      method: 'GET', headers: { Authorization: `Bearer ${token}`, Accept: 'application/pdf, application/json' },
      credentials: 'omit', redirect: 'error', cache: 'no-store', signal,
    });
    if (!response.ok) throw Object.assign(new Error('Stored report download failed.'), { status: response.status });
    if (!(response.headers.get('Content-Type') || '').toLowerCase().startsWith('application/pdf')) {
      throw new Error('The response is not a PDF.');
    }
    const blob = await response.blob();
    if (signal?.aborted) throw Object.assign(new Error('Download was cancelled.'), { name: 'AbortError' });
    const objectUrl = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    try {
      const location = String(details.location_name || 'location').normalize('NFKD').replace(/[\u0300-\u036f]/g, '')
        .replace(/[^A-Za-z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 48) || 'location';
      link.href = objectUrl;
      link.download = `product-promotion-analysis-${location}-${details.analysis_start || id}.pdf`;
      document.body.appendChild(link);
      link.click();
    } finally {
      link.remove();
      window.URL.revokeObjectURL(objectUrl);
    }
  },

  getCommercialAnalysisOptionsAsync(client) {
    return getDataAsync(client, `${Config.API_URL_REPORTS_V2_COMMERCIAL_ANALYSIS}options/`);
  },

  getCommercialAnalysisRunsAsync(client) {
    return getDataAsync(client, Config.API_URL_REPORTS_V2_COMMERCIAL_ANALYSIS);
  },

  getCommercialAnalysisRunAsync(client, id) {
    return getDataAsync(client, commercialAnalysisRunUrl(id));
  },

  postCommercialAnalysisRunAsync(client, parameters) {
    return pushDataAsync(client, Config.API_URL_REPORTS_V2_COMMERCIAL_ANALYSIS, parameters);
  },

  async downloadCommercialAnalysisPdf(client, id, signal, details) {
    const url = commercialAnalysisRunUrl(id);
    const token = client?.getAuthToken?.();
    if (!token) throw Object.assign(new Error('Authentication is required.'), { status: 401 });
    const response = await fetch(`${client.prefix || ''}${url}download/`, {
      method: 'GET', headers: { Authorization: `Bearer ${token}`, Accept: 'application/pdf, application/json' },
      credentials: 'omit', redirect: 'error', cache: 'no-store', signal,
    });
    if (!response.ok) throw Object.assign(new Error('Stored report download failed.'), { status: response.status });
    if (!(response.headers.get('Content-Type') || '').toLowerCase().startsWith('application/pdf')) {
      throw new Error('The response is not a PDF.');
    }
    const blob = await response.blob();
    if (signal?.aborted) throw Object.assign(new Error('Download was cancelled.'), { name: 'AbortError' });
    const objectUrl = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    try {
      link.href = objectUrl;
      link.download = commercialAnalysisDownloadName(id, details);
      document.body.appendChild(link);
      link.click();
    } finally {
      link.remove();
      window.URL.revokeObjectURL(objectUrl);
    }
  },

  getAverageSalesPerItemOptionsAsync(client) {
    return getDataAsync(client, Config.API_URL_REPORTS_V2_SALES_AVERAGE_PER_ITEM_OPTIONS, EMPTY_QUERY);
  },

  getAverageSalesPerItemAsync(client, queryData = EMPTY_QUERY) {
    return getDataAsync(client, Config.API_URL_REPORTS_V2_SALES_AVERAGE_PER_ITEM, queryData);
  },

  getLocations(client, queryData = EMPTY_QUERY) {
    return getData(client, Config.API_URL_REPORTS_V2_LOCATIONS_LIST, queryData);
  },

  getLocationsAsync(client, queryData = EMPTY_QUERY) {
    return getDataAsync(client, Config.API_URL_REPORTS_V2_LOCATIONS_LIST, queryData);
  },

  getLocationGroups(client, queryData = EMPTY_QUERY) {
    return getData(client, Config.API_URL_REPORTS_V2_LOCATIONS_GROUPS, queryData);
  },

  getProductsCatalogSummary(client, queryData = EMPTY_QUERY) {
    return getData(client, Config.API_URL_REPORTS_V2_PRODUCTS_CATALOG_SUMMARY, queryData);
  },

  getProductsByCategory(client, queryData = EMPTY_QUERY) {
    return getData(client, Config.API_URL_REPORTS_V2_PRODUCTS_BY_CATEGORY, queryData);
  },

  getProductsLatestImages(client, queryData = EMPTY_QUERY) {
    return getData(client, Config.API_URL_REPORTS_V2_PRODUCTS_LATEST_IMAGES, queryData);
  },

  getProductsSoldMtd(client, queryData = EMPTY_QUERY) {
    return getData(client, Config.API_URL_REPORTS_V2_PRODUCTS_SOLD_MTD, queryData);
  },

  getProductsTopShareMtd(client, queryData = EMPTY_QUERY) {
    return getData(client, Config.API_URL_REPORTS_V2_PRODUCTS_TOP_SHARE_MTD, queryData);
  },

  getProductsTopListMtd(client, queryData = EMPTY_QUERY) {
    return getData(client, Config.API_URL_REPORTS_V2_PRODUCTS_TOP_LIST_MTD, queryData);
  },

  getProductsSalesMtd(client, queryData = EMPTY_QUERY) {
    return getData(client, Config.API_URL_REPORTS_V2_PRODUCTS_SALES_MTD, queryData);
  },

  getProductsClassShareMtd(client, queryData = EMPTY_QUERY) {
    return getData(client, Config.API_URL_REPORTS_V2_PRODUCTS_CLASS_SHARE_MTD, queryData);
  },

  getProductsCategoryShareMtd(client, queryData = EMPTY_QUERY) {
    return getData(client, Config.API_URL_REPORTS_V2_PRODUCTS_CATEGORY_SHARE_MTD, queryData);
  },

  getProductsSubcategoryShareMtd(client, queryData = EMPTY_QUERY) {
    return getData(client, Config.API_URL_REPORTS_V2_PRODUCTS_SUBCATEGORY_SHARE_MTD, queryData);
  },

  getPosStatus(client, queryData = EMPTY_QUERY) {
    return getData(client, Config.API_URL_REPORTS_V2_LOCATIONS_POS_STATUS, queryData);
  },

  getPosSignalsAsync(client, queryData = EMPTY_QUERY) {
    return getDataAsync(client, Config.API_URL_REPORTS_V2_LOCATIONS_POS_SIGNALS, queryData);
  },

  getSalesLocationTimeseries(client, queryData = EMPTY_QUERY) {
    return getData(client, Config.API_URL_REPORTS_V2_SALES_LOCATION_TIMESERIES, queryData);
  },

  getSalesSummary(client, queryData = EMPTY_QUERY) {
    return getData(client, Config.API_URL_REPORTS_V2_SALES_SUMMARY, queryData);
  },

  getSalesTop(client, queryData = EMPTY_QUERY) {
    return getData(client, Config.API_URL_REPORTS_V2_SALES_TOP, queryData);
  },

  getSalesByPos(client, queryData = EMPTY_QUERY) {
    return getData(client, Config.API_URL_REPORTS_V2_SALES_BY_POS, queryData);
  },

  getSalesDailyKpi(client, queryData = EMPTY_QUERY) {
    return getData(client, Config.API_URL_REPORTS_V2_SALES_DAILY_KPI, queryData);
  },

  postSalesLocationTrends(client, queryData = EMPTY_QUERY) {
    return pushData(client, Config.API_URL_REPORTS_V2_SALES_LOCATION_TRENDS, queryData);
  },

  postSalesProductTrends(client, queryData = EMPTY_QUERY) {
    const data = pushData(client, Config.API_URL_REPORTS_V2_SALES_PRODUCT_TRENDS, queryData);
    const series = ['sales', 'salesD', 'salesD_LY', 'salesM_new', 'salesM_LY_new', 'salesW', 'salesW_LY'];
    if (!data || data.status === 'ERROR' || data.detail || data.error
        || !series.every(key => Array.isArray(data[key])
          && data[key].every(row => row && typeof row === 'object' && !Array.isArray(row)))) {
      throw new Error('Unable to load product sales. Please try again.');
    }
    return data;
  },

  getSalesByLocation(client, queryData = EMPTY_QUERY) {
    return getData(client, Config.API_URL_REPORTS_V2_SALES_BY_LOCATION, queryData);
  },

  getSalesByDeliveryChannel(client, queryData = EMPTY_QUERY) {
    return getData(client, Config.API_URL_REPORTS_V2_SALES_BY_DELIVERY_CHANNEL, queryData);
  },

  getSalesByProduct(client, queryData = EMPTY_QUERY) {
    return getData(client, Config.API_URL_REPORTS_V2_SALES_BY_PRODUCT, queryData);
  },

  getSalesProductLocationTimeseries(client, queryData = EMPTY_QUERY) {
    return getData(client, Config.API_URL_REPORTS_V2_SALES_PRODUCT_LOCATION_TIMESERIES, queryData);
  },

  getSalesByProductLocation(client, queryData = EMPTY_QUERY) {
    return getData(client, Config.API_URL_REPORTS_V2_SALES_BY_PRODUCT_LOCATION, queryData);
  },

  getSalesByCategory(client, queryData = EMPTY_QUERY) {
    return getData(client, Config.API_URL_REPORTS_V2_SALES_BY_CATEGORY, queryData);
  },

  getSalesByCategoryLocation(client, queryData = EMPTY_QUERY) {
    return getData(client, Config.API_URL_REPORTS_V2_SALES_BY_CATEGORY_LOCATION, queryData);
  },

  getSalesCategoryLocationTimeseries(client, queryData = EMPTY_QUERY) {
    return getData(client, Config.API_URL_REPORTS_V2_SALES_CATEGORY_LOCATION_TIMESERIES, queryData);
  },

  getSalesClassLocationTimeseries(client, queryData = EMPTY_QUERY) {
    return getData(client, Config.API_URL_REPORTS_V2_SALES_CLASS_LOCATION_TIMESERIES, queryData);
  },

  getSalesProductDaily(client, queryData = EMPTY_QUERY) {
    return getData(client, Config.API_URL_REPORTS_V2_SALES_PRODUCT_DAILY, queryData);
  },

  getSalesCategoryDaily(client, queryData = EMPTY_QUERY) {
    return getData(client, Config.API_URL_REPORTS_V2_SALES_CATEGORY_DAILY, queryData);
  },

  getSalesLocationValueComparison(client, queryData = EMPTY_QUERY) {
    return getData(client, Config.API_URL_REPORTS_V2_SALES_LOCATION_VALUE_COMPARISON, queryData);
  },

  getSalesLocationTransactionComparison(client, queryData = EMPTY_QUERY) {
    return getData(client, Config.API_URL_REPORTS_V2_SALES_LOCATION_TRANSACTION_COMPARISON, queryData);
  },

  getSalesForecast(client, queryData = EMPTY_QUERY) {
    return getData(client, Config.API_URL_REPORTS_V2_SALES_FORECAST, queryData);
  },

  getSalesCategoryTargets(client, queryData = EMPTY_QUERY) {
    return getData(client, Config.API_URL_REPORTS_V2_SALES_CATEGORY_TARGETS, queryData);
  },

  getSalesByPaymentType(client, queryData = EMPTY_QUERY) {
    return getData(client, Config.API_URL_REPORTS_V2_SALES_BY_PAYMENT_TYPE, queryData);
  },

  getOrdersRecent(client, queryData = EMPTY_QUERY) {
    return getData(client, Config.API_URL_REPORTS_V2_ORDERS_RECENT, queryData);
  },

  getOrdersByLocation(client, queryData = EMPTY_QUERY) {
    return getData(client, Config.API_URL_REPORTS_V2_ORDERS_BY_LOCATION, queryData);
  },

  getOrdersList(client, queryData = EMPTY_QUERY) {
    return getData(client, Config.API_URL_REPORTS_V2_ORDERS_LIST, queryData);
  },

  getOrdersCount(client, queryData = EMPTY_QUERY) {
    return getData(client, Config.API_URL_REPORTS_V2_ORDERS_COUNT, queryData);
  },

  getOrderFlowAsync(client, queryData = EMPTY_QUERY) {
    return getDataAsync(client, Config.API_URL_REPORTS_V2_ORDERS_FLOW, queryData);
  },

  getOrdersByLocationUrl() {
    return Config.API_URL_REPORTS_V2_ORDERS_BY_LOCATION;
  },

  getOrdersInvoices(client, queryData = EMPTY_QUERY) {
    return getData(client, Config.API_URL_REPORTS_V2_ORDERS_INVOICES, queryData);
  },

  getOrdersDetail(client, queryData = EMPTY_QUERY) {
    return getData(client, Config.API_URL_REPORTS_V2_ORDERS_DETAIL, queryData);
  },

  getDeliveryOrdersAsync(client, queryData = EMPTY_QUERY) {
    return getDataAsync(client, Config.API_URL_REPORTS_V2_ORDERS_DELIVERIES, queryData);
  },

  refreshDeliveryCourierAsync(client, orderIref, date) {
    return pushDataAsync(
      client,
      `${Config.API_URL_REPORTS_V2_ORDERS}${encodeURIComponent(orderIref)}/refresh-courier/`,
      { date }
    );
  },

  getClientsList(client, queryData = EMPTY_QUERY) {
    return getData(client, Config.API_URL_REPORTS_V2_CLIENTS_LIST, queryData);
  },

  getClientsListAsync(client, queryData = EMPTY_QUERY) {
    return getDataAsync(client, Config.API_URL_REPORTS_V2_CLIENTS_LIST, queryData);
  },

  getClientsEvolutionAsync(client, queryData = EMPTY_QUERY) {
    return getDataAsync(client, Config.API_URL_REPORTS_V2_CLIENTS_EVOLUTION, queryData);
  },

  getClientsDistributionsAsync(client, queryData = EMPTY_QUERY) {
    return getDataAsync(client, Config.API_URL_REPORTS_V2_CLIENTS_DISTRIBUTIONS, queryData);
  },

  getClientsActivationAsync(client, queryData = EMPTY_QUERY) {
    return getDataAsync(client, Config.API_URL_REPORTS_V2_CLIENTS_ACTIVATION, queryData);
  },

  async downloadUnvalidatedCreditClients(client) {
    const apiUser = JSON.parse(localStorage.getItem('apiUser') || '{}');
    const token = apiUser?.accessToken || apiUser?.access || apiUser?.token;
    if (!token) {
      throw new Error('Download failed because the session token was not found. Please login again.');
    }
    const response = await fetch(`${client?.prefix || ''}${Config.API_URL_REPORTS_V2_CLIENTS}unvalidated-clients-with-credit/`, {
      method: 'GET',
      headers: { Authorization: `Bearer ${token}` },
      credentials: 'include',
    });
    if (!response.ok) {
      throw new Error(response.status === 401 || response.status === 403
        ? 'Download failed because the session could not be authenticated. Please refresh the page and try again.'
        : 'Download failed. Please try again.');
    }
    const objectUrl = window.URL.createObjectURL(await response.blob());
    const link = document.createElement('a');
    link.href = objectUrl;
    link.download = 'unvalidated_clients_with_credit.csv';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(objectUrl);
  },

  getClientsSummary(client, queryData = EMPTY_QUERY) {
    return getData(client, Config.API_URL_REPORTS_V2_CLIENTS_SUMMARY, queryData);
  },

  getClientsNew(client, queryData = EMPTY_QUERY) {
    return getData(client, Config.API_URL_REPORTS_V2_CLIENTS_NEW, queryData);
  },

  getClientsActive(client, queryData = EMPTY_QUERY) {
    return getData(client, Config.API_URL_REPORTS_V2_CLIENTS_ACTIVE, queryData);
  },

  getClientsRepeat(client, queryData = EMPTY_QUERY) {
    return getData(client, Config.API_URL_REPORTS_V2_CLIENTS_REPEAT, queryData);
  },

  getClientsRegisteredAnonymousSummary(client, queryData = EMPTY_QUERY) {
    return getData(client, Config.API_URL_REPORTS_V2_CLIENTS_REGISTERED_ANONYMOUS_SUMMARY, queryData);
  },

  getClientsRegisteredAnonymousEvolution(client, queryData = EMPTY_QUERY) {
    return getData(client, Config.API_URL_REPORTS_V2_CLIENTS_REGISTERED_ANONYMOUS_EVOLUTION, queryData);
  },

  getClientsRegisteredAnonymousComparison(client, queryData = EMPTY_QUERY) {
    return getData(client, Config.API_URL_REPORTS_V2_CLIENTS_REGISTERED_ANONYMOUS_COMPARISON, queryData);
  },

  getClientsRegisteredActivityByLocation(client, queryData = EMPTY_QUERY) {
    return getData(client, Config.API_URL_REPORTS_V2_CLIENTS_REGISTERED_ACTIVITY_BY_LOCATION, queryData);
  },

  getClientsRegisteredActivityDetailedExportAsync(client, queryData = EMPTY_QUERY) {
    return getDataAsync(client, Config.API_URL_REPORTS_V2_CLIENTS_REGISTERED_ACTIVITY_DETAILED_EXPORT, queryData);
  },

  getDemographicsGender(client, queryData = EMPTY_QUERY) {
    return getData(client, Config.API_URL_REPORTS_V2_DEMOGRAPHICS_GENDER, queryData);
  },

  getDemographicsAge(client, queryData = EMPTY_QUERY) {
    return getData(client, Config.API_URL_REPORTS_V2_DEMOGRAPHICS_AGE, queryData);
  },

  getGeographyCounty(client, queryData = EMPTY_QUERY) {
    return getData(client, Config.API_URL_REPORTS_V2_GEOGRAPHY_COUNTY, queryData);
  },

  getGeographyUrbanRural(client, queryData = EMPTY_QUERY) {
    return getData(client, Config.API_URL_REPORTS_V2_GEOGRAPHY_URBAN_RURAL, queryData);
  },

  getPromotionsKpiSummary(client, queryData = EMPTY_QUERY) {
    return getData(client, Config.API_URL_REPORTS_V2_PROMOTIONS_KPI_SUMMARY, queryData);
  },

  getPromotionsKpiSummaryAsync(client, queryData = EMPTY_QUERY) {
    return getDataAsync(client, Config.API_URL_REPORTS_V2_PROMOTIONS_KPI_SUMMARY, queryData);
  },

  getPromotionsActivityEvolution(client, queryData = EMPTY_QUERY) {
    return getData(client, Config.API_URL_REPORTS_V2_PROMOTIONS_ACTIVITY_EVOLUTION, queryData);
  },

  getPromotionsActivityEvolutionAsync(client, queryData = EMPTY_QUERY) {
    return getDataAsync(client, Config.API_URL_REPORTS_V2_PROMOTIONS_ACTIVITY_EVOLUTION, queryData);
  },

  getPromotionsPerformance(client, queryData = EMPTY_QUERY) {
    return getData(client, Config.API_URL_REPORTS_V2_PROMOTIONS_PERFORMANCE, queryData);
  },

  getPromotionsPerformanceAsync(client, queryData = EMPTY_QUERY) {
    return getDataAsync(client, Config.API_URL_REPORTS_V2_PROMOTIONS_PERFORMANCE, queryData);
  },

  getPromotionsOpenClaims(client, queryData = EMPTY_QUERY) {
    return getData(client, Config.API_URL_REPORTS_V2_PROMOTIONS_OPEN_CLAIMS, queryData);
  },

  getPromotionsOpenClaimsAsync(client, queryData = EMPTY_QUERY) {
    return getDataAsync(client, Config.API_URL_REPORTS_V2_PROMOTIONS_OPEN_CLAIMS, queryData);
  },

  getPromotionsOrdersByLocation(client, queryData = EMPTY_QUERY) {
    return getData(client, Config.API_URL_REPORTS_V2_PROMOTIONS_ORDERS_BY_LOCATION, queryData);
  },

  getPromotionsOrdersByLocationAsync(client, queryData = EMPTY_QUERY) {
    return getDataAsync(client, Config.API_URL_REPORTS_V2_PROMOTIONS_ORDERS_BY_LOCATION, queryData);
  },

  getPromotionsDetailedExportAsync(client, queryData = EMPTY_QUERY) {
    return getDataAsync(client, Config.API_URL_REPORTS_V2_PROMOTIONS_DETAILED_EXPORT, queryData);
  },

  getMarginsByProduct(client, queryData = EMPTY_QUERY) {
    return getData(client, Config.API_URL_REPORTS_V2_MARGINS_BY_PRODUCT, queryData);
  },

  getMarginsByProductLocations(client, queryData = EMPTY_QUERY) {
    return getData(client, Config.API_URL_REPORTS_V2_MARGINS_BY_PRODUCT_LOCATIONS, queryData);
  },

  getMarginsByProductUrl() {
    return Config.API_URL_REPORTS_V2_MARGINS_BY_PRODUCT;
  },

  getMarginsByProductLocationsUrl() {
    return Config.API_URL_REPORTS_V2_MARGINS_BY_PRODUCT_LOCATIONS;
  },

  getMarginsPvm(client, queryData = EMPTY_QUERY) {
    return getData(client, Config.API_URL_REPORTS_V2_MARGINS_PVM, queryData);
  },

  getMarginsPvmSales(client, queryData = EMPTY_QUERY) {
    return getData(client, Config.API_URL_REPORTS_V2_MARGINS_PVM_SALES, queryData);
  },

  getMarginsPvmUrl() {
    return Config.API_URL_REPORTS_V2_MARGINS_PVM;
  },

  getMarginsPvmSalesUrl() {
    return Config.API_URL_REPORTS_V2_MARGINS_PVM_SALES;
  },

  getExportsList(client, queryData = EMPTY_QUERY) {
    return getData(client, Config.API_URL_REPORTS_V2_EXPORTS, queryData);
  },

  getExportsListAsync(client, queryData = EMPTY_QUERY) {
    return getDataAsync(client, Config.API_URL_REPORTS_V2_EXPORTS, queryData);
  },

  getExportsDownloadUrl(fileName) {
    if (!fileName) return '';
    return `${Config.API_URL_REPORTS_V2_EXPORTS_DOWNLOAD}?file=${encodeURIComponent(fileName)}`;
  },

  postExportSalesProductsByDate(client, queryData = EMPTY_QUERY) {
    return pushData(client, Config.API_URL_REPORTS_V2_EXPORTS_SALES_PRODUCTS_BY_DATE, queryData);
  },

  postExportSalesProductsByDateAsync(client, queryData = EMPTY_QUERY) {
    return pushDataAsync(client, Config.API_URL_REPORTS_V2_EXPORTS_SALES_PRODUCTS_BY_DATE, queryData);
  },

  postExportSalesLocationsByDate(client, queryData = EMPTY_QUERY) {
    return pushData(client, Config.API_URL_REPORTS_V2_EXPORTS_SALES_LOCATIONS_BY_DATE, queryData);
  },

  postExportSalesLocationsByDateAsync(client, queryData = EMPTY_QUERY) {
    return pushDataAsync(client, Config.API_URL_REPORTS_V2_EXPORTS_SALES_LOCATIONS_BY_DATE, queryData);
  },

  postExportSalesProductsByMonth(client, queryData = EMPTY_QUERY) {
    return pushData(client, Config.API_URL_REPORTS_V2_EXPORTS_SALES_PRODUCTS_BY_MONTH, queryData);
  },

  postExportSalesProductsByMonthAsync(client, queryData = EMPTY_QUERY) {
    return pushDataAsync(client, Config.API_URL_REPORTS_V2_EXPORTS_SALES_PRODUCTS_BY_MONTH, queryData);
  },

  postExportSalesLocationsByMonth(client, queryData = EMPTY_QUERY) {
    return pushData(client, Config.API_URL_REPORTS_V2_EXPORTS_SALES_LOCATIONS_BY_MONTH, queryData);
  },

  postExportSalesLocationsByMonthAsync(client, queryData = EMPTY_QUERY) {
    return pushDataAsync(client, Config.API_URL_REPORTS_V2_EXPORTS_SALES_LOCATIONS_BY_MONTH, queryData);
  },

  postExportSalesProductsDelivery(client, queryData = EMPTY_QUERY) {
    return pushData(client, Config.API_URL_REPORTS_V2_EXPORTS_SALES_PRODUCTS_DELIVERY, queryData);
  },

  postExportSalesProductsDeliveryAsync(client, queryData = EMPTY_QUERY) {
    return pushDataAsync(client, Config.API_URL_REPORTS_V2_EXPORTS_SALES_PRODUCTS_DELIVERY, queryData);
  },

  postExportCompanySalesDetail(client, queryData = EMPTY_QUERY) {
    return pushData(client, Config.API_URL_REPORTS_V2_EXPORTS_COMPANY_SALES_DETAIL, queryData);
  },

  postExportCompanySalesDetailAsync(client, queryData = EMPTY_QUERY) {
    return pushDataAsync(client, Config.API_URL_REPORTS_V2_EXPORTS_COMPANY_SALES_DETAIL, queryData);
  },

  get(client, url, queryData = EMPTY_QUERY) {
    return getData(client, url, queryData);
  },

  post(client, url, queryData = EMPTY_QUERY) {
    return pushData(client, url, queryData);
  },
};

export default BackofficeReportsApi;
